// scale.ts — shared tuning metadata used by BOTH panel versions and the randomizer.
//
// Two things live here so v1.0 (Tweakpane) and v1.1 (double/halve) agree:
//   • PARAM_HINTS  — the sane [min,max,step] for every numeric param (slider bounds + randomize).
//   • intensity    — the per-effect "normalized 1.0" knob. Each effect multiplies its characteristic
//                    punch param by intensity(feel.effect). 1.0 = the tuned baseline; double/halve
//                    the knob for coarse moves (Sid Meier: big moves first), then refine the raw
//                    params underneath.

export interface Hint {
  min: number;
  max: number;
  step?: number;
}

export const PARAM_HINTS: Record<string, Hint> = {
  intensity: { min: 0.1, max: 4, step: 0.05 },
  ms: { min: 0, max: 300, step: 5 },
  volume: { min: 0, max: 1, step: 0.01 },
  validFreq: { min: 40, max: 1200 },
  invalidFreq: { min: 30, max: 600 },
  flaggedFreq: { min: 30, max: 600 },
  rootFreq: { min: 80, max: 660 },
  overshoot: { min: 0, max: 4, step: 0.05 },
  settleMs: { min: 60, max: 900, step: 10 },
  durationMs: { min: 60, max: 1200, step: 10 },
  magnitude: { min: 0, max: 40, step: 0.5 },
  decayMs: { min: 60, max: 900, step: 10 },
  scaleFrom: { min: 1, max: 2.5, step: 0.05 },
  staggerMs: { min: 0, max: 120, step: 1 },
  count: { min: 0, max: 120, step: 1 },
  speed: { min: 0, max: 800, step: 10 },
  spread: { min: 0, max: 3, step: 0.05 },
  gravity: { min: 0, max: 2500, step: 10 },
  lifeMs: { min: 100, max: 2000, step: 10 },
  size: { min: 1, max: 16, step: 1 },
  semitones: { min: 0, max: 5, step: 0.5 },
  noteMs: { min: 20, max: 200, step: 2 },
  // personality
  activation: { min: 0, max: 1, step: 0.05 },
  emoteChance: { min: 0, max: 1, step: 0.05 },
  flirtChance: { min: 0, max: 1, step: 0.05 },
  holdMs: { min: 200, max: 2500, step: 50 },
  reactDelayMs: { min: 0, max: 1200, step: 25 },
  standMs: { min: 0, max: 1500, step: 25 },
  windupMs: { min: 0, max: 1500, step: 25 },
  travelMs: { min: 0, max: 1500, step: 25 },
  reactMs: { min: 0, max: 1500, step: 25 },
  sitMs: { min: 0, max: 1500, step: 25 },
  resolveMs: { min: 0, max: 1500, step: 25 },
  particleCount: { min: 0, max: 30, step: 1 },
  particleScale: { min: 0.3, max: 3, step: 0.1 },
};

/** Waveform choices for the randomizer's "completely random" pass. */
export const WAVEFORMS: OscillatorType[] = ["sine", "square", "sawtooth", "triangle"];

// Global juice boost — the "Big Juice" mash button cranks this so every effect (sound, tween, tile
// pop, pitch, particles…) reads a bigger intensity for that one fire, then it's restored.
let juiceBoost = 1;
export function setJuiceBoost(x: number): void {
  juiceBoost = x;
}

/** The per-effect intensity multiplier (defaults to 1.0 if absent), times the global juice boost. */
export function intensity(cfg: { intensity?: number }): number {
  return (cfg.intensity ?? 1) * juiceBoost;
}

// Global juice SPEED — the JUICE rail's Slower/Faster scale every juice effect's timing (independent
// of the personality reel speed). 1 = normal, <1 = slower (longer durations), >1 = faster. Effects
// DIVIDE their durations by this. Separate from the personality reel speed on purpose (Jason wants
// to slow one without the other).
let juiceSpeed = 1;
export function setJuiceSpeed(x: number): void {
  juiceSpeed = Math.max(0.25, Math.min(4, x));
}
export function getJuiceSpeed(): number {
  return juiceSpeed;
}
/** Scale a duration (ms) by the current juice speed: slower = longer, faster = shorter. */
export function timed(ms: number): number {
  return ms / juiceSpeed;
}

/** Round a value to the precision implied by its hint's step (keeps ints int, etc.). */
export function roundToStep(value: number, hint?: Hint): number {
  const step = hint?.step ?? 0.01;
  const snapped = Math.round(value / step) * step;
  // Kill floating-point tails like 0.30000000000000004.
  const decimals = (String(step).split(".")[1] || "").length;
  return Number(snapped.toFixed(decimals));
}
